import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { Role } from '../../models/role.model';
import { RoleService } from '../../services/role.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { ServiceGroup } from '../../models/service-group.model';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-role-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    Select,
    InputText,
    Tooltip,
    ConfirmDialog
  ],
  templateUrl: './role-management.component.html',
  styleUrl: './role-management.component.scss',
  providers: [ConfirmationService]
})
export class RoleManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  roles: Role[] = [];
  filteredRoles: Role[] = [];
  selectedRole: Role = this.getEmptyRole();
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';
  selectedServiceGroupFilter: string | null = null;
  serviceGroups: ServiceGroup[] = [];
  fieldTouched: Record<string, boolean> = {};

  rows: number = 50;
  first: number = 0;
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalRolesCount(): number { return this.roles.length; }
  get activeRolesCount(): number { return this.roles.filter(r => r.isActive).length; }
  get inactiveRolesCount(): number { return this.roles.filter(r => !r.isActive).length; }

  constructor(
    private roleService: RoleService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by title or description...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Role');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadServiceGroups();
    this.loadRoles();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.showAddDialog());
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.exportToExcel());
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!(user?.isAdmin || user?.isPc));
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private loadServiceGroups(): void {
    this.serviceGroupService.getAllServiceGroups().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (groups) => {
        this.serviceGroups = (groups || []).filter(sg => sg.isActive === true);
      },
      error: () => {
        this.serviceGroups = [];
      }
    });
  }

  loadRoles() {
    this.loading = true;
    this.roleService.getAllRoles().pipe(
      catchError(error => {
        console.error('Error loading roles:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load roles. Please refresh the page or try again later.'
        });
        this.loading = false;
        // Return empty array to prevent complete failure
        return of([] as Role[]);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        // Ensure isActive is set for all roles (default to true if missing)
        this.roles = data.map(r => ({
          ...r,
          isActive: r.isActive !== undefined ? r.isActive : true
        }));
        // Reapply filters to preserve filtered state
        this.applySearch();
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.roles];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(r =>
        r.title?.toLowerCase().includes(searchLower) ||
        r.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(r => r.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(r => r.isActive === false);
      }
    }

    // Apply Service Group filter
    if (this.selectedServiceGroupFilter) {
      filtered = filtered.filter(r => r.serviceGroupId === this.selectedServiceGroupFilter);
    }

    this.filteredRoles = filtered;
    this.totalRecords = this.filteredRoles.length;
    this.first = 0;
    this.pageHeaderService.setExportDisabled(this.filteredRoles.length === 0);
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applySearch();
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  onServiceGroupFilterChange() {
    this.applySearch();
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active';
    this.selectedServiceGroupFilter = null;
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() ||
      (this.selectedStatusFilter !== null && this.selectedStatusFilter !== 'active') ||
      this.selectedServiceGroupFilter);
  }

  toggleActiveStatus(role: Role) {
    const activating = !role.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${role.title}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        const updatedRole = { ...role, isActive: !role.isActive };
        this.roleService.updateRole(role.id, updatedRole).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Role ${updatedRole.isActive ? 'activated' : 'deactivated'} successfully`
            });
            this.loadRoles();
          },
          error: (error) => {
            console.error('Error updating role status:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update role status'
            });
          }
        });
      }
    });
  }

  showAddDialog() {
    this.selectedRole = this.getEmptyRole();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  showEditDialog(role: Role) {
    this.selectedRole = { ...role };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  hideDialog() {
    this.displayDialog = false;
    this.fieldTouched = {};
    this.selectedRole = this.getEmptyRole();
  }

  resetRoleForm() {
    this.selectedRole = this.getEmptyRole();
    this.fieldTouched = {};
  }

  exportToExcel() {
    if (this.filteredRoles.length === 0) return;
    const headers = ['Title', 'Description', 'Service Group', 'Status'];
    const rows = this.filteredRoles.map(r => [
      r.title ?? '',
      r.description ?? '',
      r.serviceGroupName ?? '',
      r.isActive ? 'Active' : 'Inactive'
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Roles');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `roles_${this.formatDateForFile()}.xlsx`);
  }

  formatDateForFile(): string {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
  }

  saveRole() {
    this.fieldTouched['title'] = true;
    if (!this.selectedRole.title.trim()) {
      return;
    }

    if (this.dialogMode === 'add') {
      this.roleService.addRole(this.selectedRole).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Role added successfully'
          });
          this.loadRoles();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error adding role:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add role'
          });
        }
      });
    } else {
      this.roleService.updateRole(this.selectedRole.id, this.selectedRole).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Role updated successfully'
          });
          this.loadRoles();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating role:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update role'
          });
        }
      });
    }
  }

  private getEmptyRole(): Role {
    return {
      id: '',
      title: '',
      description: '',
      isActive: true,
      serviceGroupId: null,
      serviceGroupName: null
    };
  }

  onServiceGroupChange(): void {
    if (this.selectedRole.serviceGroupId) {
      const serviceGroup = this.serviceGroups.find(sg => sg.id === this.selectedRole.serviceGroupId);
      this.selectedRole.serviceGroupName = serviceGroup?.name || null;
    } else {
      this.selectedRole.serviceGroupName = null;
    }
  }
}

